/**
 * CorrectionRequestDialog
 *
 * Inline-Dialog im EmployeeTimeDrawer: fuer einen einzelnen Tag
 * (typisch „Keine Buchung" oder „Teilweise gearbeitet") einen
 * Korrekturantrag stellen.
 *
 * - Hat der Tag eine bestehende Buchung (`timeEntryId`): wird mit
 *   `time_entry_id` gepostet (Update-Antrag).
 * - Sonst: Neuantrag mit `employee_id` + `requested_date`.
 *
 * Pflichtfelder: Grund (>= 3 Zeichen). Zeiten optional.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { timeTrackingAPI } from '../../lib/api';
import { Send } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Checkbox } from '../../components/ui/checkbox';

export default function CorrectionRequestDialog({
  open, onOpenChange,
  employeeId, employeeName,
  date,                  // YYYY-MM-DD
  timeEntryId = null,    // optional, wenn bestehender Eintrag
  defaultClockIn = '',
  defaultClockOut = '',
  defaultBreakMinutes = 0,
  weeklyHours = null,
  onSubmitted,
}) {
  const [clockIn, setClockIn] = useState(defaultClockIn);
  const [clockOut, setClockOut] = useState(defaultClockOut);
  const [breakMinutes, setBreakMinutes] = useState(defaultBreakMinutes);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('time_tracking', 'edit');
  const [applyImmediately, setApplyImmediately] = useState(false);

  const dailyTargetMin = weeklyHours ? Math.round((weeklyHours / 5) * 60) : null;
  const [planned, setPlanned] = useState(null);
  const [absence, setAbsence] = useState(null);

  useEffect(() => {
    if (!open || !employeeId || !date) { setPlanned(null); setAbsence(null); return; }
    let active = true;
    timeTrackingAPI.getPlannedShift(employeeId, date)
      .then((res) => { if (active) { setPlanned(res.data?.found ? res.data : null); setAbsence(res.data?.absence || null); } })
      .catch(() => { if (active) { setPlanned(null); setAbsence(null); } });
    return () => { active = false; };
  }, [open, employeeId, date]);

  const applyPlanned = () => {
    if (!planned) return;
    if (planned.start_time) setClockIn(planned.start_time);
    if (planned.end_time) setClockOut(planned.end_time);
    setBreakMinutes(planned.pause_minutes || 0);
  };
  const hhmmToMin = (t) => { if (!t) return null; const [h, m] = t.split(':').map(Number); return h * 60 + m; };
  const minToHHMM = (min) => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
  const minToLabel = (min) => `${Math.floor(min / 60)}:${String(min % 60).padStart(2, '0')} Std`;
  const suggestEnd = () => {
    const start = hhmmToMin(clockIn);
    if (start == null || dailyTargetMin == null) return;
    setClockOut(minToHHMM(Math.min(23 * 60 + 59, start + dailyTargetMin + Number(breakMinutes || 0))));
  };

  useEffect(() => {
    if (open) {
      setClockIn(defaultClockIn || '');
      setClockOut(defaultClockOut || '');
      setBreakMinutes(defaultBreakMinutes || 0);
      setReason('');
      setApplyImmediately(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const formattedDate = date ? format(parseISO(date), 'EEEE, dd.MM.yyyy', { locale: de }) : '';

  const handleSubmit = async () => {
    if (reason.trim().length < 3) {
      toast.error('Bitte einen Grund angeben (mind. 3 Zeichen)');
      return;
    }
    setBusy(true);
    try {
      const toIso = (t) => (t ? `${date}T${t}:00` : null);
      const payload = {
        reason: reason.trim(),
        requested_date: date,
        requested_clock_in: toIso(clockIn),
        requested_clock_out: toIso(clockOut),
        requested_break_minutes: breakMinutes ? Number(breakMinutes) : null,
        apply_immediately: canEdit && applyImmediately,
      };
      if (timeEntryId) payload.time_entry_id = timeEntryId;
      else payload.employee_id = employeeId;

      await timeTrackingAPI.requestCorrection(payload);
      toast.success(payload.apply_immediately ? 'Zeit direkt nachgetragen' : 'Korrekturantrag gesendet');
      onSubmitted?.();
      onOpenChange(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Antrag konnte nicht gesendet werden';
      toast.error(typeof msg === 'string' ? msg : 'Antrag konnte nicht gesendet werden');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" onCloseAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Korrekturantrag stellen</DialogTitle>
          <DialogDescription>
            {employeeName ? `${employeeName} · ${formattedDate}` : formattedDate}
            {!timeEntryId && (
              <span className="block mt-1 text-amber-600">Neuantrag — für diesen Tag existiert noch keine Buchung.</span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 py-2">
          <div>
            <Label htmlFor="cr-clock-in">Beginn</Label>
            <Input
              id="cr-clock-in"
              type="time"
              value={clockIn}
              onChange={(e) => setClockIn(e.target.value)}
              data-testid="cr-clock-in"
            />
          </div>
          <div>
            <Label htmlFor="cr-clock-out">Ende</Label>
            <Input
              id="cr-clock-out"
              type="time"
              value={clockOut}
              onChange={(e) => setClockOut(e.target.value)}
              data-testid="cr-clock-out"
            />
          </div>
          <div className="col-span-2">
            <Label htmlFor="cr-break">Pause (Minuten)</Label>
            <Input
              id="cr-break"
              type="number"
              min="0"
              max="240"
              value={breakMinutes}
              onChange={(e) => setBreakMinutes(e.target.value)}
              data-testid="cr-break"
            />
          </div>
          {absence && (
            <div className="col-span-2 flex items-start gap-2 rounded-md border border-amber-300/70 bg-amber-50 dark:bg-amber-950/30 px-2.5 py-1.5 text-xs text-amber-800 dark:text-amber-300" data-testid="cr-absence-warning">
              <span className="font-medium">Achtung:</span>
              <span>Für diesen Tag ist eine genehmigte Abwesenheit hinterlegt (<span className="font-semibold">{absence.label}</span>). Bitte prüfen, ob eine Zeiterfassung wirklich gewünscht ist.</span>
            </div>
          )}
          {planned && (
            <div className="col-span-2 flex items-center justify-between rounded-md border border-sky-300/60 bg-sky-50 dark:bg-sky-950/30 px-2.5 py-1.5 text-xs" data-testid="cr-planned-shift">
              <span className="text-sky-800 dark:text-sky-300">
                Geplanter Einsatz: <span className="font-semibold">{planned.start_time}–{planned.end_time}</span>
                {planned.pause_minutes ? ` · Pause ${planned.pause_minutes} Min` : ''}
                {planned.massnahme_name ? ` · ${planned.massnahme_name}` : ''}
              </span>
              <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={applyPlanned} data-testid="cr-apply-planned">
                Übernehmen
              </Button>
            </div>
          )}
          {dailyTargetMin != null && (
            <div className="col-span-2 flex items-center justify-between rounded-md border border-border/60 bg-secondary/30 px-2.5 py-1.5 text-xs" data-testid="cr-target-hours">
              <span className="text-muted-foreground">Tages-Soll: <span className="font-medium text-foreground">{minToLabel(dailyTargetMin)}</span></span>
              <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs" disabled={!clockIn} onClick={suggestEnd} data-testid="cr-suggest-end">
                Endzeit vorschlagen
              </Button>
            </div>
          )}
          <div className="col-span-2">
            <Label htmlFor="cr-reason">Grund <span className="text-rose-500">*</span></Label>
            <Textarea
              id="cr-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="z. B. Stempeluhr vergessen / Dienstgang ohne App"
              rows={3}
              data-testid="cr-reason"
            />
          </div>
        </div>

        {canEdit && (
          <label className="flex items-start gap-2 rounded-md border border-border/60 bg-secondary/30 p-2.5 cursor-pointer" data-testid="cr-apply-immediately-wrap">
            <Checkbox
              checked={applyImmediately}
              onCheckedChange={(v) => setApplyImmediately(!!v)}
              data-testid="cr-apply-immediately"
              className="mt-0.5"
            />
            <span className="text-xs leading-snug">
              <span className="font-medium">Sofort nachtragen (ohne Genehmigung)</span>
              <span className="block text-muted-foreground">Der Eintrag wird direkt gespeichert und revisionssicher als genehmigt protokolliert.</span>
            </span>
          </label>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Abbrechen
          </Button>
          <Button onClick={handleSubmit} disabled={busy} data-testid="cr-submit">
            {busy ? 'Sende...' : <span className="flex items-center gap-2"><Send className="h-4 w-4" />{canEdit && applyImmediately ? 'Sofort nachtragen' : 'Antrag senden'}</span>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
